import { alpha } from "@material-ui/system";
import { styled } from "@material-ui/core/styles";
import { Stack, StackProps, IconButtonProps, IconButton } from "@material-ui/core";
import { LeftIcon, RightIcon } from "./CarouselIcon";
import useSettings from "hooks/useSettings";
import { preventBubbling } from "utils/listings/helper";
import type { IconifyProps } from "./Iconify";

type StyledIconButtonProps = IconButtonProps & {
  filled?: boolean;
  shape?: "circular" | "rounded";
  hasChildren?: boolean;
};

const StyledIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "filled" && prop !== "hasChildren" && prop !== "shape"
})<StyledIconButtonProps>(({ filled, shape, hasChildren, theme }) => ({
  color: "inherit",
  transition: theme.transitions.create("all", {
    duration: theme.transitions.duration.shorter
  }),
  ...(shape === "rounded" && {
    borderRadius: Number(theme.shape.borderRadius) * 1.5
  }),
  ...(!filled && {
    opacity: 0.48,
    "&:hover": {
      opacity: 1
    }
  }),
  ...(filled && {
    color: alpha(theme.palette.common.white, 0.8),
    backgroundColor: alpha(theme.palette.grey[900], 0.48),
    "&:hover": {
      color: theme.palette.common.white,
      backgroundColor: theme.palette.grey[900]
    }
  }),
  ...(hasChildren && {
    zIndex: 9,
    top: "50%",
    position: "absolute",
    marginTop: theme.spacing(-2)
  })
}));

// ----------------------------------------------------------------------

type CarouselArrowProps = StackProps & {
  size?: "small" | "medium" | "large";
  shape?: "circular" | "rounded";
  length?: number;
  filled?: boolean;
  children?: React.ReactNode;
  icon?: IconifyProps; // Right icon
  leftButtonProps?: IconButtonProps;
  rightButtonProps?: IconButtonProps;
  onNext?: () => void;
  onPrevious?: () => void;
};

export function CarouselArrows({
  size,
  length,
  shape = "circular",
  filled = false,
  icon,
  children,
  leftButtonProps,
  rightButtonProps,
  onNext,
  onPrevious,
  ...other
}: CarouselArrowProps): JSX.Element {
  const { themeDirection } = useSettings();

  if (length === 1) return <>{children}</>;

  const isRTL = themeDirection === "rtl";

  const hasChildren = !!children;

  if (hasChildren) {
    return (
      <Stack {...other}>
        <StyledIconButton
          size={size}
          filled={filled}
          shape={shape}
          hasChildren={!!children}
          onClick={preventBubbling(onPrevious)}
          {...leftButtonProps}
          sx={{
            left: 16,
            ...leftButtonProps?.sx
          }}
        >
          <LeftIcon icon={icon} isRTL={isRTL} />
        </StyledIconButton>
        {children}
        <StyledIconButton
          size={size}
          filled={filled}
          shape={shape}
          hasChildren={!!children}
          onClick={preventBubbling(onNext)}
          {...rightButtonProps}
          sx={{
            right: 16,
            ...rightButtonProps?.sx
          }}
        >
          <RightIcon icon={icon} isRTL={isRTL} />
        </StyledIconButton>
      </Stack>
    );
  }

  return (
    <Stack direction="row" alignItems="center" display="inline-flex" {...other}>
      <StyledIconButton
        size={size}
        filled={filled}
        shape={shape}
        onClick={preventBubbling(onPrevious)}
        {...leftButtonProps}
      >
        <LeftIcon icon={icon} isRTL={isRTL} />
      </StyledIconButton>
      <StyledIconButton
        size={size}
        filled={filled}
        shape={shape}
        onClick={preventBubbling(onNext)}
        {...rightButtonProps}
      >
        <RightIcon icon={icon} isRTL={isRTL} />
      </StyledIconButton>
    </Stack>
  );
}
